/**
 * Developer state override.
 *
 * Lets us preview the app in different auth/subscription states (logged-out,
 * logged-in not subscribed, subscribed for N weeks) without touching the real
 * Firebase backend. The override is stored in localStorage and applied on top
 * of the real `useAuth()` value.
 *
 * Real auth is still wired up underneath — toggling this only changes what
 * components reading `useAuth()` see. Set state to "real" to disable the
 * override entirely.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { isLocalDevHost } from "@/lib/dev-mode";
import { clearLibraryCaches } from "@/lib/library-cache";

const WEEK_MS = 7 * 86_400_000;

/** Fast-forward platform time in dev mode by the simulated subscription tenure. */
export function computeDevSimulatedNow(
  state: DevState,
  subscribedWeeks: number | null,
): Date | null {
  if (state === "real" || subscribedWeeks == null) return null;
  return new Date(Date.now() + subscribedWeeks * WEEK_MS);
}

export type DevState =
  | "real"
  | "logged-out"
  | "logged-in"
  | "subscribed-1w"
  | "subscribed-40w"
  | "subscribed-300w";

export const DEV_STATE_OPTIONS: { value: DevState; label: string }[] = [
  { value: "real", label: "Real auth" },
  { value: "logged-out", label: "Logged out" },
  { value: "logged-in", label: "Logged in, not subscribed" },
  { value: "subscribed-1w", label: "Subscribed · 1 week" },
  { value: "subscribed-40w", label: "Subscribed · 40 weeks" },
  { value: "subscribed-300w", label: "Subscribed · 300 weeks" },
];

export type DevTier = "basic" | "plus" | "vip" | "library-only";

export const DEV_TIER_OPTIONS: { value: DevTier; label: string }[] = [
  { value: "basic", label: "Basic" },
  { value: "plus", label: "Plus" },
  { value: "vip", label: "VIP (Super Sub)" },
  { value: "library-only", label: "Library Only" },
];

export type DevMembershipType = "legacy" | "v2";

export const DEV_MEMBERSHIP_TYPE_OPTIONS: { value: DevMembershipType; label: string }[] = [
  { value: "legacy", label: "OG Legacy (full catalog)" },
  { value: "v2", label: "V2 (weekly release)" },
];

const STORAGE_KEY = "rp.devState";
const TIER_STORAGE_KEY = "rp.devTier";
const MEMBERSHIP_TYPE_STORAGE_KEY = "rp.devMembershipType";

type DevStateContextValue = {
  state: DevState;
  setState: (s: DevState) => void;
  /** Weeks subscribed for the active dev state, or null when not applicable. */
  subscribedWeeks: number | null;
  /** Convenience: true when the dev state simulates an active subscription. */
  isSubscribed: boolean;
  /** Convenience: true when the dev state simulates being signed in. */
  isSignedIn: boolean;
  /** Simulated membership tier (only meaningful when isSubscribed). */
  tier: DevTier;
  setTier: (t: DevTier) => void;
  /**
   * Simulated `users/{uid}.membershipType`. Defaults to `'v2'`. Returns
   * `undefined` when no subscription is being simulated so the UI can branch
   * off the same "is this person actually entitled?" check.
   */
  membershipType: DevMembershipType | undefined;
  setMembershipType: (t: DevMembershipType) => void;
  /**
   * Dev-only platform clock advance. Null when using real auth or when no
   * subscription tenure is being simulated.
   */
  simulatedNow: Date | null;
};

const DevStateContext = createContext<DevStateContextValue | null>(null);

export function DevStateProvider({ children }: { children: ReactNode }) {
  const [state, setStateRaw] = useState<DevState>("real");
  const [tier, setTierRaw] = useState<DevTier>("basic");
  const [membershipTypeRaw, setMembershipTypeRaw] =
    useState<DevMembershipType>("v2");

  // Hydrate from localStorage on mount (client-only, avoids SSR mismatch).
  // Dev overrides are localhost-only; clear stale keys on deployed hosts.
  useEffect(() => {
    try {
      if (!isLocalDevHost()) {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(TIER_STORAGE_KEY);
        window.localStorage.removeItem(MEMBERSHIP_TYPE_STORAGE_KEY);
        return;
      }
      const stored = window.localStorage.getItem(STORAGE_KEY) as DevState | null;
      if (stored) setStateRaw(stored);
      const storedTier = window.localStorage.getItem(TIER_STORAGE_KEY) as DevTier | null;
      if (
        storedTier === "basic" ||
        storedTier === "plus" ||
        storedTier === "vip" ||
        storedTier === "library-only"
      ) {
        setTierRaw(storedTier);
      }
      const storedMembershipType = window.localStorage.getItem(
        MEMBERSHIP_TYPE_STORAGE_KEY,
      ) as DevMembershipType | null;
      if (storedMembershipType === "legacy" || storedMembershipType === "v2") {
        setMembershipTypeRaw(storedMembershipType);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const setState = useCallback((s: DevState) => {
    setStateRaw(s);
    clearLibraryCaches();
    try {
      window.localStorage.setItem(STORAGE_KEY, s);
    } catch {
      /* ignore */
    }
  }, []);

  const setTier = useCallback((t: DevTier) => {
    setTierRaw(t);
    clearLibraryCaches();
    try {
      window.localStorage.setItem(TIER_STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
  }, []);

  const setMembershipType = useCallback((t: DevMembershipType) => {
    setMembershipTypeRaw(t);
    clearLibraryCaches();
    try {
      window.localStorage.setItem(MEMBERSHIP_TYPE_STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<DevStateContextValue>(() => {
    const subscribedWeeks =
      state === "subscribed-1w"
        ? 1
        : state === "subscribed-40w"
          ? 40
          : state === "subscribed-300w"
            ? 300
            : null;
    const isSubscribed = subscribedWeeks !== null;
    const isSignedIn = isSubscribed || state === "logged-in";
    const simulatedNow = computeDevSimulatedNow(state, subscribedWeeks);
    return {
      state,
      setState,
      subscribedWeeks,
      isSubscribed,
      isSignedIn,
      tier,
      setTier,
      // Only expose the simulated membership type when a subscription is
      // being simulated — otherwise it's not a meaningful concept.
      membershipType: isSubscribed ? membershipTypeRaw : undefined,
      setMembershipType,
      simulatedNow,
    };
  }, [state, setState, tier, setTier, membershipTypeRaw, setMembershipType]);

  return (
    <DevStateContext.Provider value={value}>
      {children}
    </DevStateContext.Provider>
  );
}

export function useDevState(): DevStateContextValue {
  const ctx = useContext(DevStateContext);
  if (!ctx)
    throw new Error("useDevState must be used inside <DevStateProvider>");
  return ctx;
}
